import React, { useState, useEffect } from "react";
import Popup from "../Popup";
import FormHeader from "../form/Header";
import { useTranslation } from "react-i18next";
import FormRepeater from "../form/FormRepeater";
import MessageError from "../MessageError";
import { Link } from "react-router-dom";
import CustomTooltip from "../CustomTooltip";
import { DeleteIcon, LoadingIcon, PrintIcon } from "../../assets/icons/Icons";
import { ValidateCard } from "../../api/sell-orders/validateCard";
import OfficesSelect from "../form/OfficesSelect";
import FormSection from "../form/Section";
import Message from "../Message";
import { CheckCardBalance } from "../../api/sell-orders/checkCardBalance";
import usePrint from "../../hooks/usePrint";
import { combineCard } from "../../api/sell-orders/combineCard";
import SkipCombineFee from "../SkipCombineFee";
import { GetProgramFees } from "../../api";
import { CentsToDollars } from "../../utils/formatPrice";
import { validateSourceCard } from "../../api/sell-orders/validateSourceCard";
import { getCardByPrn } from "../../api/sell-orders/getCardByPrn";
import { getCombineCardReceipt } from "../../api/sell-orders/getCombineCardReceipt";

const CombineCardModal = ({ onClose }) => {
  const { t } = useTranslation();
  const [officeId, setOfficeId] = useState(null);
  const [cardsToReplace, setCardsToReplace] = useState([]);
  const [cardsToReplaceWith, setCardsToReplaceWith] = useState([]);
  const [cardsReplaced, setCardsReplaced] = useState(null);
  const [disabledReplaceButton, setDisabledReplaceButton] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [skipCombineFee, setSkipCombineFee] = useState(false);
  const [combineFee, setcombineFee] = useState(null);
  const [program_id, setProgramId] = useState(null);

  const handleCombineCard = async () => {
    setError("");
    setLoading(true);
    const isUniqueSourceCards = cardsToReplace.every(
      (card, index) =>
        cardsToReplace.findIndex(
          (c) => c.processor_reference === card.processor_reference
        ) === index
    )
    if (isUniqueSourceCards && cardsToReplace.length >= 2 && cardsToReplaceWith && officeId) {
      try {
        const { data: id } = await combineCard({
          office_id: officeId,
          source_cards: cardsToReplace.map((c) => ({
            processor_reference: c.processor_reference,
          })),
          destination_processor_reference:
            cardsToReplaceWith[0].processor_reference,
          skip_combine_fee: skipCombineFee,
        });
        setCardsReplaced(id);
        setCardsToReplaceWith([]);
        setCardsToReplace([]);
      } catch (err) {
        setError(err.message);
        console.log("Error fetching data: " + err);
      }
    } else if(!isUniqueSourceCards) {
      setError(t("Source cards must be unique."));
    } else if (cardsToReplace.length < 2) {
      setError(t("Combine Card requires at least two cards."));
    } else {
      setError(t("Please fill all required fields."));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (
      cardsToReplace[0]?.card_type &&
      cardsToReplace[0]?.processor_reference &&
      cardsToReplaceWith[0]?.card_type &&
      cardsToReplaceWith[0]?.processor_reference
    ) {
      setDisabledReplaceButton(false);
    } else {
      setDisabledReplaceButton(true);
    }
  }, [cardsToReplace, cardsToReplaceWith]);

  useEffect(() => {
    async function getProgramFees() {
      // get program fees
      try {
        const programFeeRes = await GetProgramFees({
          program_id: program_id,
        });
        setcombineFee(programFeeRes?.data?.sell_order_combine_card_fee ?? null);
      } catch (error) {
        console.log(error);
      }
    }
    if (program_id) {
      getProgramFees();
    }
  }, [program_id]);

  const { handlePrint } = usePrint(getCombineCardReceipt);

  return (
    <Popup size="large" withClose withOverlay onClose={onClose}>
      <FormHeader title={t("Combine Cards")}></FormHeader>

      <div className="row mb-3 mt-4">
        <div className="col-xs-12 col-md-6">
          <FormSection title={t("Select an Office")} required>
            <OfficesSelect
              onChange={setOfficeId}
              defaultOfficeIds={[officeId]}
            />
          </FormSection>
        </div>
      </div>

      <div className="mb-3">
        <h5 className="mb-0">{t("Source Card Details")}</h5>
        <hr className="size-3" />

        <FormRepeater
          columns={[
            t("Proxy Number"),
            "",
            t("Product Description"),
            t("Balance"),
            t("Clear"),
          ]}
          maxRows={5}
          addRowText={t("Add Card")}
          RenderComponent={CardRow}
          onRowChange={setCardsToReplace}
          rowData={cardsToReplace}
          officeId={officeId}
          sourceValidation={true}
          setProgramId={setProgramId}
        />
      </div>

      <div className="mb-3">
        <h5 className="mb-0">{t("Destination Card Details")}</h5>
        <hr className="size-3" />

        <FormRepeater
          columns={[
            t("Proxy Number"),
            "",
            t("Product Description"),
            t("Balance"),
            t("Clear"),
          ]}
          maxRows={1}
          addRowText={t("Add Card")}
          RenderComponent={CardRow}
          onRowChange={setCardsToReplaceWith}
          rowData={cardsToReplaceWith}
          officeId={officeId}
        />
      </div>

      <SkipCombineFee
        initialValue={skipCombineFee}
        onChange={(e) => setSkipCombineFee(e.target.checked ? true : false)}
      />
      <p className="mb-3">Combine Fee: <b>${CentsToDollars(combineFee)}</b></p>
      <MessageError error={error} className="text-center" />

      {cardsReplaced && (
        <>
          <Message
            message={t("Card successfully combined!")}
            type="message-success"
            className="mb-2"
          />
          <button
            className="btn btn-medium btn-secondary"
            onClick={(e) => {
              e.preventDefault();
              handlePrint({
                requestParams: { id: cardsReplaced.id },
                name: `order-${cardsReplaced.id}`,
                width: "",
                height: "",
                download: false,
              });
            }}
          >
            <PrintIcon />
            {t("Print Receipt")}
          </button>
        </>
      )}

      {!cardsReplaced && (
        <button
          className={`btn btn-primary btn-medium ${
            loading ? "btn-loading" : ""
          }`}
          disabled={disabledReplaceButton || loading}
          onClick={handleCombineCard}
        >
          {loading && <LoadingIcon />} {t("Combine Cards")}
        </button>
      )}
    </Popup>
  );
};

export default CombineCardModal;

const CardRow = ({ rowData, rowIndex, onFieldChange, removeRow, ...props }) => {
  const { t } = useTranslation();
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [validationLoading, setValidationLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCheckButton = async () => {
    setValidationLoading(true);

    if (props.sourceValidation) {
      try {
        const [response, cardRes] = await Promise.all([
          validateSourceCard({
            office_id: props.officeId,
            processor_reference: rowData.processor_reference,
          }),
          getCardByPrn({ prn: rowData.processor_reference }),
        ]);
        if (response?.error?.message) {
          setError(response.error.message);
        } else {
          props.setProgramId &&
            props.setProgramId(cardRes?.data?.accounts?.[0]?.program_id ?? null);
          const {
            data: { balance },
          } = await CheckCardBalance({ prn: rowData.processor_reference });
          onFieldChange({
            card_type: "Oxford Gift Card PLUS",
            balance: balance,
          });
          setError("");
        }
      } catch (err) {
        // A 401/500 (e.g. no write) or a failed card lookup rejects here; show
        // the message instead of crashing the page.
        setError(err?.message || t("Unable to validate card."));
        console.log("Error validating source card: " + err);
      } finally {
        setValidationLoading(false);
      }
      return;
    }

    setError("");
    onFieldChange("card_type", "");
    try {
      const response = await ValidateCard({
        processor_reference: rowData.processor_reference,
        office_id: props.officeId,
      });
      if (response?.error?.message) {
        setError(response.error.message);
        setValidationLoading(false);
      } else {
        try {
          const {
            data: { balance },
          } = await CheckCardBalance({ prn: rowData.processor_reference });
          onFieldChange({
            card_type: "Oxford Gift Card PLUS",
            balance: balance,
          });
          setError("");
        } catch (err) {
          console.log("Error fetching data: " + err);
        }
      }
    } catch (err) {
      console.log("Error fetching data: " + err);
    }

    setValidationLoading(false);
  };

  useEffect(() => {
    onFieldChange("card_type", "");
    if (rowData.processor_reference?.length === 12 && props.officeId) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
    // eslint-disable-next-line
  }, [rowData.processor_reference]);

  return (
    <>
      <td>
        <input
          type="number"
          placeholder={t("Proxy Number")}
          value={rowData.processor_reference || ""}
          onChange={(e) => onFieldChange("processor_reference", e.target.value)}
          onWheel={(e) => e.target.blur()}
          onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
        />
        <MessageError error={error} />
      </td>
      <td>
        <button
          className={`btn btn-primary btn-small ${
            validationLoading ? "btn-loading" : ""
          }`}
          onClick={handleCheckButton}
          disabled={buttonDisabled}
        >
          {validationLoading ? <LoadingIcon /> : ""}
          {t("Validate")}
        </button>
      </td>
      <td>
        <input
          type="text"
          placeholder={t("Card Type")}
          value={rowData.card_type || ""}
          onChange={(e) => onFieldChange("card_type", e.target.value)}
          disabled
        />
      </td>
      <td>
        <input
          type="text"
          placeholder={t("Balance")}
          value={rowData.balance || ""}
          onChange={(e) => onFieldChange("balance", e.target.value)}
          disabled
        />
      </td>
      <td className="text-center">
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            removeRow(rowIndex);
          }}
        >
          <CustomTooltip icon={<DeleteIcon />} message={t("Remove Row")} />
        </Link>
      </td>
    </>
  );
};
